import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Overlay, Card, Title, Subtitle } from "./styles";
import { H6, TaglineG } from "../../styles/stylesTypography";
import ButtonV2 from "../button/button_v2";
import { createPortal } from "react-dom";
import api from "../../services/api";

/**
 * FunctionBlockedOverlay
 * 
 * Props:
 * - visible: boolean (exibir/ocultar overlay)
 * - onActivate: function (chamado ao clicar; aqui você abre seu modal)
 * - message: string (opcional; padrão: "Função bloqueada para seu plano")
 * - mode: "cover-parent" | "fullscreen" (padrão: "cover-parent")
 * - blur: string (padrão: "8px")
 * - zIndex: number (padrão: 9000)
 * - blockScroll: boolean (padrão: true quando mode === "fullscreen")
 * 
 * Observação:
 * - Em "cover-parent", coloque o container pai com `position: relative;`
 *   para o overlay cobrir apenas aquela área.
 */
const FunctionBlockedOverlay = ({
  visible = true,
  onActivate = () => {},
  message = "Função bloqueada para seu plano",
  feature,
  mode = "cover-parent",
  blur = "8px",
  zIndex = 9000,
  blockScroll,
  attachTo,
  ctaUrl = "/compra?5",
  ctaType = "redirect",
}) => {
  const isFullscreen = mode === "fullscreen";
  const shouldBlockScroll = blockScroll ?? isFullscreen;
  const upsellFiredRef = useRef(false);

  const handleUpgrade = () => {
    try {
      window.location.href = `${ctaUrl}`;
    } catch (_) {
      window.location.href = ctaUrl;
    }
  };

  const handleCta = () => {
    if (ctaType === "activate") {
      onActivate();
      return;
    }

    handleUpgrade();
  };

  useLayoutEffect(() => {
    if (!attachTo?.current || isFullscreen) return;
    const el = attachTo.current;
    const prev = { position: el.style.position };
    const cs = window.getComputedStyle(el);
    if (!['relative','absolute','fixed','sticky'].includes(cs.position)) {
      el.style.position = 'relative';
    }
    return () => {
      if (!['relative','absolute','fixed','sticky'].includes(cs.position)) {
        el.style.position = prev.position;
      }
    };
  }, [attachTo, isFullscreen]);

  useEffect(() => {
    if (!visible || !feature || upsellFiredRef.current) return;
    upsellFiredRef.current = true;
    const isLogged = localStorage.getItem("@logged");
    if (isLogged) {
      api.post('/alertas/upsell', { feature }).catch(() => {});
    }
  }, [visible, feature]);

  useEffect(() => {
    if (!shouldBlockScroll) return;
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [visible, shouldBlockScroll]);

  if (!visible) return null;
  if (!isFullscreen && !attachTo?.current) return null;

  const overlayProps = isFullscreen ? { $isFullscreen: true } : { $isFullscreen: false };

  const overlayElement = (
    <Overlay
      $blur={blur}
      $zIndex={zIndex}
      {...overlayProps}
    >
      <Card style={{ pointerEvents: "auto" }}>
        <TaglineG>{message}</TaglineG>
        <ButtonV2 width="150px" value="Desbloquear" onClick={handleCta} style={{ pointerEvents: "auto" }}>
          <H6>Ativar</H6>
        </ButtonV2>
      </Card>
    </Overlay>
  );

  if (!isFullscreen && attachTo?.current) {
    return createPortal(overlayElement, attachTo.current);
  }

  return overlayElement;
};

export default FunctionBlockedOverlay;
